import type { Account } from '@interfaces/account';
import { ApplyForGovFunds } from '@interfaces/account';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import { Box } from '@ovComponents/1-primative/box/box';
import { Grid } from '@ovComponents/1-primative/grid/grid';
import { Typography } from '@ovComponents/1-primative/typography/typography';
import { Card, CardContent } from '@ovComponents/2-component/card/card';
import { IconButton } from '@ovComponents/2-component/iconButton/iconButton';
import { AffiliationTable } from '@ovComponents/4-module/workflowCompletion/subSteps/account/affiliationTable';
import { GoalLinkingReview } from '@ovComponents/4-module/workflowCompletion/subSteps/account/goalLinking';
import {
  getJointAffiliate,
  getSortedAffiliations,
  sortedAffiliationRelations,
} from '@ovComponents/4-module/workflowCompletion/subSteps/account/utils';
import { useTranslation } from 'react-i18next';
import { getAcccountDetailComponent } from '../account.reviewItem';

type RespSingleReviewItemProps = {
  account: Account;
  onDeleteAccount: () => void;
};
export const JointRespSingleReviewItem = ({
  account,
  onDeleteAccount,
}: RespSingleReviewItemProps) => {
  const { t } = useTranslation([
    'affiliationTypes',
    'components',
    'workflowCompletions',
  ]);
  const [yes, no] = [
    t('components:booleanOptions.yes'),
    t('components:booleanOptions.no'),
  ];
  const hasSuccessor = Boolean(account.affiliations?.length);
  const sortedAffiliations = getSortedAffiliations(
    account.affiliations ?? [],
    'relation',
    sortedAffiliationRelations,
  );

  const accountDetailsConfig = [
    {
      field: t('workflowCompletions:jointAccountHolder.label'),
      value: getJointAffiliate(account) ? yes : no,
    },
    {
      field: t(
        'workflowCompletions:accountReviewPanel.basicCanadaEducationSavingsGrant',
      ),
      value: account.applyForGovFunds?.includes(ApplyForGovFunds.BASIC_CESG)
        ? yes
        : no,
    },
    {
      field: t('workflowCompletions:accountReviewPanel.canadaLearningBond'),
      value: account.applyForGovFunds?.includes(ApplyForGovFunds.CLB)
        ? yes
        : no,
    },
    {
      field: t('workflowCompletions:accountReviewPanel.additionalCESG'),
      value: account.applyForGovFunds?.includes(
        ApplyForGovFunds.ADDITIONAL_CESG,
      )
        ? yes
        : no,
    },
    {
      field: t('workflowCompletions:accountReviewPanel.affiliate'),
      value: hasSuccessor ? yes : no,
    },
  ];
  return (
    <Card variant='outlined' sx={{ mb: 2 }}>
      <CardContent>
        <Grid container gap={3}>
          <Grid
            item
            container
            justifyContent='space-between'
            alignItems='center'
          >
            <Grid item>
              <Typography variant='bodyLarge' weight='bold'>
                {t('workflowCompletions:accountReviewPanel.jointRespSingle')}
              </Typography>
            </Grid>
            {!account.id && (
              <Grid item display='flex' justifyContent='end'>
                <IconButton
                  onClick={onDeleteAccount}
                  aria-label='close'
                  size='small'
                >
                  <CloseRoundedIcon />
                </IconButton>
              </Grid>
            )}
          </Grid>
          <Grid item container gap={1}>
            {accountDetailsConfig.map((config) => (
              <Grid
                item
                xs={12}
                display='flex'
                flexDirection='row'
                justifyContent='space-between'
              >
                {getAcccountDetailComponent(config)}
              </Grid>
            ))}
          </Grid>
          <Grid item xs={12}>
            <AffiliationTable
              affiliations={sortedAffiliations}
              showType={true}
              showRelation={true}
            />
          </Grid>
          <Grid item xs={12}>
            <Box
              display='flex'
              flexDirection='row'
              justifyContent='space-between'
            >
              <GoalLinkingReview account={account} />
            </Box>
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );
};

export default JointRespSingleReviewItem;
